<template>
  <!-- https://tracker.globetrack.ie/issues/11520 (AIS-07) - cross-tenant AI
       usage and cost. Its filters are its own, not the Setup tab's tenant.
       v-tab-item renders lazily, so nothing loads until the tab is opened. -->
  <div class="pt-6">
    <!-- filter bar: one row that wraps -->
    <v-card outlined class="ai-card mb-6">
      <div class="filter-bar d-flex flex-wrap align-center px-4 py-3">
        <v-text-field
          v-model="from"
          type="date"
          label="From"
          outlined
          dense
          hide-details
          class="filter-date"
        ></v-text-field>
        <v-text-field
          v-model="to"
          type="date"
          label="To"
          outlined
          dense
          hide-details
          class="filter-date"
        ></v-text-field>
        <div class="preset-group d-flex">
          <v-btn
            v-for="p in presets"
            :key="p.delta"
            small
            depressed
            :outlined="preset !== p.delta"
            :color="preset === p.delta ? 'primary' : undefined"
            class="preset-btn"
            @click="setMonth(p.delta)"
          >
            {{ p.label }}
          </v-btn>
        </div>
        <v-autocomplete
          :value="domain"
          :items="tenantItems"
          :loading="$apollo.queries.owners.loading"
          label="Tenant"
          outlined
          dense
          hide-details
          :clearable="!!domain"
          class="filter-tenant"
          @change="(v) => (domain = v || '')"
        ></v-autocomplete>
        <v-select
          :value="module"
          :items="moduleItems"
          label="Module"
          outlined
          dense
          hide-details
          :clearable="!!module"
          class="filter-module"
          @change="(v) => (module = v || '')"
        ></v-select>
      </div>
      <v-progress-linear
        :active="$apollo.queries.summary.loading"
        indeterminate
        absolute
        bottom
        height="2"
      ></v-progress-linear>
    </v-card>

    <v-alert v-if="!datesValid" type="warning" dense text class="mb-6">
      Pick a From date on or before the To date.
    </v-alert>
    <v-alert v-else-if="errorMessage" type="error" dense text class="mb-6">
      {{ errorMessage }}
    </v-alert>

    <!-- KPI tiles: the row fills evenly for 3 or 4 tiles -->
    <div class="mb-6">
      <v-row>
        <v-col
          v-for="card in tiles"
          :key="card.label"
          cols="12"
          sm="6"
          :md="12 / tiles.length"
        >
          <v-card outlined class="ai-card fill-height">
            <div class="d-flex align-center pa-4">
              <v-avatar
                size="44"
                :color="tileBg(card.color)"
                class="mr-4 flex-shrink-0"
              >
                <v-icon :color="card.color">{{ card.icon }}</v-icon>
              </v-avatar>
              <div class="min-w-0">
                <div class="ai-overline" :class="mutedClass">
                  {{ card.label }}
                </div>
                <div class="headline font-weight-medium tabular kpi-value">
                  {{ card.value }}
                </div>
                <div class="caption kpi-hint" :class="mutedClass">
                  {{ card.hint || " " }}
                </div>
              </div>
            </div>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- daily cost -->
    <v-card outlined class="ai-card mb-6">
      <div class="d-flex align-center px-4 pt-4">
        <span class="subtitle-1 font-weight-medium">Daily cost (USD)</span>
        <v-spacer></v-spacer>
        <span v-if="hasData" class="caption tabular" :class="mutedClass">
          {{ fmtCost(totals.costUsd) }} over {{ days.length }} day{{
            days.length === 1 ? "" : "s"
          }}
        </span>
      </div>
      <div class="chart-box pa-4">
        <div v-if="hasData" class="fill-height">
          <bar-chart
            :chart-data="dailyChartData"
            :options="chartOptions"
            :height="240"
          ></bar-chart>
        </div>
        <div v-else class="ai-empty fill-height">
          <v-avatar size="56" :color="tileBg('primary')" class="mb-3">
            <v-icon color="primary">mdi-chart-bar</v-icon>
          </v-avatar>
          <div class="subtitle-2">No AI calls in this period</div>
          <div class="caption" :class="mutedClass">
            Pick another date range, tenant or module.
          </div>
        </div>
      </div>
    </v-card>

    <!-- breakdown -->
    <v-card outlined class="ai-card mb-6">
      <v-tabs v-model="groupTab" background-color="transparent" class="px-2">
        <v-tab v-for="g in groupings" :key="g.key">By {{ g.key }}</v-tab>
      </v-tabs>
      <v-divider></v-divider>
      <v-tabs-items v-model="groupTab" class="transparent">
        <v-tab-item v-for="g in groupings" :key="g.key">
          <v-data-table
            :headers="groupHeaders(g)"
            :items="g.rows"
            :loading="$apollo.queries.summary.loading"
            :items-per-page="10"
            :hide-default-footer="g.rows.length <= 10"
            item-key="key"
            class="usage-table"
            :class="{ 'ai-usage-clickable': g.key === 'tenant' }"
            loading-text="Loading... Please wait"
            @click:row="(item) => onGroupRowClick(g, item)"
          >
            <template v-slot:no-data>
              <div class="py-6 caption" :class="mutedClass">
                <v-icon small class="mr-1" :color="mutedIconColor">
                  mdi-inbox
                </v-icon>
                No AI calls in this period.
              </div>
            </template>
            <template v-slot:item.label="{ item }">
              <span v-if="g.key !== 'tenant'">{{ item.label }}</span>
              <a
                v-else
                href="#"
                title="Show this tenant only"
                @click.prevent.stop="drillInto(item)"
                >{{ item.label }}</a
              >
              <div
                v-if="g.key === 'tenant' && item.label !== item.key"
                class="caption"
                :class="mutedClass"
              >
                {{ item.key }}
              </div>
            </template>
            <template v-slot:item.calls="{ item }">
              <span class="tabular">{{ fmtInt(item.calls) }}</span>
            </template>
            <template v-slot:item.errors="{ item }">
              <span class="tabular" :class="{ 'error--text': item.errors > 0 }">
                {{ fmtInt(item.errors) }}
              </span>
            </template>
            <template v-slot:item.cappedCalls="{ item }">
              <span class="tabular">{{ fmtInt(item.cappedCalls) }}</span>
            </template>
            <template v-slot:item.tokens="{ item }">
              <span class="tabular" :title="tokenTitle(item)">{{
                fmtInt(item.tokens)
              }}</span>
            </template>
            <template v-slot:item.costUsd="{ item }">
              <span class="tabular font-weight-medium">{{
                fmtCost(item.costUsd)
              }}</span>
            </template>
          </v-data-table>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- recent calls -->
    <v-card outlined class="ai-card">
      <div class="d-flex align-center px-4 py-2 recent-head">
        <span class="subtitle-1 font-weight-medium">Recent calls</span>
        <span
          v-if="events && events.totalCount"
          class="caption ml-2 tabular"
          :class="mutedClass"
        >
          {{ fmtInt(events.totalCount) }}
        </span>
        <v-spacer></v-spacer>
        <v-switch
          v-model="onlyErrors"
          dense
          inset
          hide-details
          color="error"
          class="mt-0 pt-0"
        >
          <template v-slot:label>
            <span class="body-2">Errors only</span>
          </template>
        </v-switch>
      </div>
      <v-divider></v-divider>
      <v-data-table
        :headers="eventHeaders"
        :items="eventRows"
        :loading="$apollo.queries.events.loading"
        item-key="id"
        disable-pagination
        disable-sort
        hide-default-footer
        class="usage-table events-table"
        loading-text="Loading... Please wait"
      >
        <template v-slot:no-data>
          <div class="py-6 caption" :class="mutedClass">
            <v-icon small class="mr-1" :color="mutedIconColor">
              {{ onlyErrors ? "mdi-check-circle-outline" : "mdi-history" }}
            </v-icon>
            {{
              onlyErrors
                ? "No failed AI calls in this period."
                : "No AI calls in this period."
            }}
          </div>
        </template>
        <template v-slot:item.time="{ item }">
          <span class="tabular text-no-wrap">{{ item.time }}</span>
        </template>
        <template v-slot:item.tenant="{ item }">
          <span :title="item.domain">{{ item.tenantName || item.domain }}</span>
        </template>
        <template v-slot:item.tokens="{ item }">
          <span class="tabular" :title="tokenTitle(item)">{{
            fmtInt(item.tokens)
          }}</span>
        </template>
        <template v-slot:item.costUsd="{ item }">
          <span class="tabular">{{ fmtCost(item.costUsd) }}</span>
        </template>
        <template v-slot:item.duration="{ item }">
          <span class="tabular text-no-wrap">{{ item.duration }}</span>
        </template>
        <template v-slot:item.status="{ item }">
          <v-tooltip v-if="item.status === 'error'" bottom max-width="480">
            <template v-slot:activator="{ on }">
              <v-chip v-on="on" x-small color="error" text-color="white">
                <v-icon x-small left>mdi-alert-circle</v-icon>error
              </v-chip>
            </template>
            <span>{{ item.error || "No error text." }}</span>
          </v-tooltip>
          <v-chip
            v-else-if="item.status === 'capped'"
            x-small
            color="orange darken-2"
            text-color="white"
            :title="item.error"
          >
            <v-icon x-small left>mdi-lock-outline</v-icon>capped
          </v-chip>
          <v-chip v-else x-small color="green" text-color="white">
            <v-icon x-small left>mdi-check</v-icon>ok
          </v-chip>
        </template>
      </v-data-table>
      <template v-if="events && events.totalCount">
        <v-divider></v-divider>
        <v-card-actions class="px-4">
          <span class="caption" :class="mutedClass">
            Showing {{ fmtInt(eventRows.length) }} of
            {{ fmtInt(events.totalCount) }}
          </span>
          <v-spacer></v-spacer>
          <v-btn
            v-if="events.pageInfo.hasNextPage"
            small
            text
            color="primary"
            :loading="loadingMore"
            @click="loadMoreEvents"
          >
            Load more
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>
  </div>
</template>

<script>
import moment from "moment";
import BarChart from "../../dashboard/charts/BarChart";
import {
  AI_SETUP_OWNERS_QUERY,
  AI_MODULE_REGISTRY_QUERY,
  AI_USAGE_SUMMARY_QUERY,
  AI_USAGE_EVENTS_QUERY,
} from "./ai-setup-graphql";
import {
  monthRange,
  fillDays,
  formatCost,
  formatInt,
  errorRate,
  formatDuration,
  eventStatus,
  sourceLabel,
  shortDay,
} from "./ai-usage-helpers";

const PAGE_SIZE = 50;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

// The four aliased summaries repeat the same message; show it once.
const gqlError = (e) =>
  e && e.graphQLErrors && e.graphQLErrors.length
    ? e.graphQLErrors
        .map((g) => g.message)
        .filter((m, i, all) => all.indexOf(m) === i)
        .join(", ")
    : (e && e.message) || "Request failed.";

const COLUMN_HEADERS = [
  { text: "Calls", value: "calls", align: "end", width: 120 },
  { text: "Errors", value: "errors", align: "end", width: 120 },
  { text: "Capped", value: "cappedCalls", align: "end", width: 120 },
  { text: "Tokens", value: "tokens", align: "end", width: 140 },
  { text: "Cost", value: "costUsd", align: "end", width: 140 },
];

export default {
  name: "ai-setup-usage-tab",

  components: { BarChart },

  data() {
    const range = monthRange(new Date());
    return {
      from: range.from,
      to: range.to,
      // "" = all; v-select keeps "" as a real selection, null would not show.
      domain: "",
      module: "",
      onlyErrors: false,
      groupTab: 0,
      owners: null,
      registry: null,
      summary: null,
      events: null,
      loadingMore: false,
      errorMessage: "",
      presets: [
        { delta: 0, label: "This month" },
        { delta: -1, label: "Last month" },
      ],
      eventHeaders: [
        { text: "Time", value: "time", width: 160 },
        { text: "Tenant", value: "tenant" },
        { text: "User", value: "userName" },
        { text: "Module", value: "moduleLabel" },
        { text: "Call", value: "callName" },
        { text: "Model", value: "model" },
        { text: "Tokens", value: "tokens", align: "end", width: 100 },
        { text: "Cost", value: "costUsd", align: "end", width: 100 },
        { text: "Duration", value: "duration", align: "end", width: 100 },
        { text: "Status", value: "status", width: 100 },
        { text: "Source", value: "source" },
      ],
    };
  },

  apollo: {
    owners: {
      query: AI_SETUP_OWNERS_QUERY,
    },
    registry: {
      query: AI_MODULE_REGISTRY_QUERY,
      update: (data) => data.aiModuleRegistry,
    },
    summary: {
      query: AI_USAGE_SUMMARY_QUERY,
      fetchPolicy: "network-only",
      variables() {
        return this.filterVariables;
      },
      skip() {
        return !this.datesValid;
      },
      update: (data) => data,
      result() {
        this.errorMessage = "";
      },
      error(e) {
        this.errorMessage = gqlError(e);
      },
    },
    events: {
      query: AI_USAGE_EVENTS_QUERY,
      fetchPolicy: "network-only",
      variables() {
        return {
          ...this.filterVariables,
          onlyErrors: this.onlyErrors,
          first: PAGE_SIZE,
          after: null,
        };
      },
      skip() {
        return !this.datesValid;
      },
      update: (data) => data.aiUsageEvents,
      error(e) {
        this.errorMessage = gqlError(e);
      },
    },
  },

  computed: {
    mutedClass() {
      return this.$vuetify.theme.dark
        ? "grey--text text--lighten-1"
        : "grey--text text--darken-1";
    },
    mutedIconColor() {
      return this.$vuetify.theme.dark ? "grey lighten-1" : "grey";
    },
    // Which month preset the From/To dates match, if any (highlights it).
    preset() {
      const now = new Date();
      const hit = this.presets.find((p) => {
        const r = monthRange(now, p.delta);
        return r.from === this.from && r.to === this.to;
      });
      return hit ? hit.delta : null;
    },
    datesValid() {
      return (
        DATE_RE.test(this.from || "") &&
        DATE_RE.test(this.to || "") &&
        this.from <= this.to
      );
    },
    filterVariables() {
      return {
        from: this.from,
        to: this.to,
        domain: this.domain || null,
        module: this.module || null,
      };
    },
    tenantNames() {
      const names = {};
      const edges = (this.owners && this.owners.edges) || [];
      edges.forEach((e) => {
        const d = (e.node.domain || "").trim();
        if (d && !names[d]) names[d] = (e.node.name || "").trim() || d;
      });
      return names;
    },
    tenantItems() {
      const items = Object.keys(this.tenantNames)
        .map((d) => ({ text: `${this.tenantNames[d]} (${d})`, value: d }))
        .sort((a, b) => a.text.localeCompare(b.text));
      // A drilled-into domain with no owner row still needs an item to show.
      if (this.domain && !this.tenantNames[this.domain]) {
        items.unshift({ text: this.domain, value: this.domain });
      }
      return [{ text: "All tenants", value: "" }, ...items];
    },
    moduleItems() {
      const modules = (this.registry || []).map((m) => ({
        text: m.label,
        value: m.key,
      }));
      return [{ text: "All modules", value: "" }, ...modules];
    },
    totals() {
      return this.summary && this.summary.byTenant
        ? this.summary.byTenant.totals
        : null;
    },
    hasData() {
      return !!(this.totals && this.totals.calls > 0);
    },
    tiles() {
      const t = this.totals || { calls: 0, errors: 0, cappedCalls: 0 };
      const tiles = [
        {
          label: "Total cost (USD)",
          value: formatCost(t.costUsd),
          icon: "mdi-currency-usd",
          color: "primary",
        },
        {
          label: "Calls",
          value: formatInt(t.calls),
          icon: "mdi-swap-horizontal",
          color: "indigo",
        },
        {
          label: "Error rate",
          value: errorRate(t.errors, t.calls),
          hint: `${formatInt(t.errors)} error${t.errors === 1 ? "" : "s"}`,
          icon: "mdi-alert-circle-outline",
          color: t.errors > 0 ? "error" : "success",
        },
      ];
      if (t.cappedCalls > 0) {
        tiles.push({
          label: "Capped calls",
          value: formatInt(t.cappedCalls),
          hint: "Skipped, monthly cap reached",
          icon: "mdi-lock-outline",
          color: "orange darken-2",
        });
      }
      return tiles;
    },
    // The summary echoes the dates it used; fall back to the inputs.
    days() {
      if (!this.summary || !this.summary.byDay) return [];
      const from = this.summary.byTenant.from || this.from;
      const to = this.summary.byTenant.to || this.to;
      return fillDays(from, to, this.summary.byDay.rows);
    },
    dailyChartData() {
      return {
        labels: this.days.map((d) => shortDay(d.day)),
        datasets: [
          {
            label: "Cost (USD)",
            backgroundColor: this.$vuetify.theme.currentTheme.primary,
            data: this.days.map((d) => d.costUsd),
            calls: this.days.map((d) => d.calls),
            maxBarThickness: 24,
          },
        ],
      };
    },
    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: {
          mode: "index",
          intersect: false,
          callbacks: {
            label: (item, data) => {
              const calls = data.datasets[0].calls[item.index];
              return `${formatCost(item.yLabel)} - ${formatInt(calls)} call${
                calls === 1 ? "" : "s"
              }`;
            },
          },
        },
        scales: {
          xAxes: [
            {
              ticks: { maxTicksLimit: 16, autoSkip: true, maxRotation: 0 },
              gridLines: { display: false },
            },
          ],
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                maxTicksLimit: 5,
                // whole cents on the axis unless the scale is under a cent
                callback: (v) =>
                  v === 0 || Math.abs(v) >= 0.01
                    ? `$${Number(v).toFixed(2)}`
                    : formatCost(v),
              },
            },
          ],
        },
      };
    },
    groupings() {
      const s = this.summary || {};
      const rows = (g) =>
        (g ? g.rows : []).map((r) => ({
          ...r,
          tokens: r.promptTokens + r.completionTokens,
        }));
      return [
        { key: "tenant", label: "Tenant", rows: rows(s.byTenant) },
        { key: "module", label: "Module", rows: rows(s.byModule) },
        { key: "model", label: "Model", rows: rows(s.byModel) },
      ];
    },
    eventRows() {
      const edges = (this.events && this.events.edges) || [];
      return edges.map(({ node }) => ({
        ...node,
        time: node.createdAt
          ? moment(node.createdAt).format("YYYY-MM-DD HH:mm:ss")
          : "",
        tokens: node.promptTokens + node.completionTokens,
        duration: formatDuration(node.durationMs),
        status: eventStatus(node),
        source: sourceLabel(node),
      }));
    },
  },

  methods: {
    fmtCost: formatCost,
    fmtInt: formatInt,
    setMonth(delta) {
      const range = monthRange(new Date(), delta);
      this.from = range.from;
      this.to = range.to;
    },
    // Light tint behind a tile icon; a plain dark tint in the dark theme.
    tileBg(color) {
      if (this.$vuetify.theme.dark) return "grey darken-3";
      const base = color === "primary" ? "blue" : color.split(" ")[0];
      return `${base} lighten-5`;
    },
    tokenTitle(item) {
      return `Prompt ${formatInt(item.promptTokens)} / completion ${formatInt(
        item.completionTokens
      )}`;
    },
    groupHeaders(g) {
      return [{ text: g.label, value: "label" }, ...COLUMN_HEADERS];
    },
    onGroupRowClick(g, item) {
      if (g.key === "tenant") this.drillInto(item);
    },
    drillInto(item) {
      if (!item.key || item.key === this.domain) return;
      this.domain = item.key;
    },
    loadMoreEvents() {
      if (!this.events || !this.events.pageInfo.hasNextPage) return;
      this.loadingMore = true;
      this.$apollo.queries.events
        .fetchMore({
          variables: { after: this.events.pageInfo.endCursor },
          updateQuery: (previous, { fetchMoreResult }) => {
            if (!fetchMoreResult) return previous;
            const next = fetchMoreResult.aiUsageEvents;
            return {
              aiUsageEvents: {
                ...next,
                edges: [...previous.aiUsageEvents.edges, ...next.edges],
              },
            };
          },
        })
        .catch((e) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: gqlError(e),
            color: "error",
          });
        })
        .finally(() => {
          this.loadingMore = false;
        });
    },
  },
};
</script>

<style scoped>
.min-w-0 {
  min-width: 0;
}
.filter-bar {
  gap: 12px;
}
.filter-date {
  flex: 0 0 165px;
  max-width: 165px;
}
.filter-tenant {
  flex: 1 1 260px;
  max-width: 380px;
}
.filter-module {
  flex: 0 1 220px;
  min-width: 180px;
}
.preset-group .preset-btn {
  height: 40px !important;
  letter-spacing: 0.04em;
}
.preset-group .preset-btn:first-child {
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
}
.preset-group .preset-btn:last-child {
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
  margin-left: -1px;
}
.kpi-value {
  line-height: 1.3;
}
.kpi-hint {
  line-height: 1.3;
}
.chart-box {
  height: 272px;
}
.recent-head {
  min-height: 52px;
}
.usage-table >>> th {
  white-space: nowrap;
}
.usage-table >>> td {
  font-size: 0.8125rem !important;
}
.ai-usage-clickable >>> tbody tr {
  cursor: pointer;
}
</style>



// WEBPACK FOOTER //
// src/components/gt/admin/ai-setup/AiSetupUsageTab.vue